import * as React from "react"

/**
 * Radix layer-dismiss race guard, shared by the modal dialog-family primitives
 * (dialog.tsx, alert-dialog.tsx).
 *
 * The dependency tree resolves MULTIPLE copies of
 * @radix-ui/react-dismissable-layer (a hoisted one for menus/popovers and
 * alert-dialog, plus copies nested under react-dialog / react-select /
 * react-context-menu). Each copy keeps its own module-level
 * `originalBodyPointerEvents` and its own layer set. When a modal content
 * mounts while ANOTHER copy's layer still holds the body lock (e.g. a dialog
 * from a different copy, or a dropdown menu whose controlled close is deferred
 * a macrotask), this content's copy captures `pointer-events: none` as the
 * body's "original" value — and faithfully restores that on unmount, leaving
 * every click app-wide dead with nothing open. On unmount, after other layers'
 * own cleanup has run, clear a stuck lock iff no open Radix layer remains.
 */
export function useClearStuckBodyPointerEventsOnUnmount() {
  React.useEffect(() => {
    return () => {
      window.setTimeout(() => {
        // The deferred macrotask deliberately outlives the unmount — but in a
        // torn-down test environment (vitest/jsdom) the globals are already
        // gone by the time it fires, which surfaced as uncaught
        // "document is not defined" suite errors under load. No-op then.
        if (typeof document === "undefined" || !document.body) return;
        const stuck =
          document.body.style.pointerEvents === "none" ||
          document.documentElement.style.pointerEvents === "none";
        if (!stuck) return;
        const openLayer = document.querySelector(
          '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="menu"][data-state="open"], [role="listbox"][data-state="open"]'
        );
        if (openLayer) return;
        document.body.style.pointerEvents = "";
        document.documentElement.style.pointerEvents = "";
      }, 0);
    };
  }, []);
}
